
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Splash } from '@client/shared/design/Splash.tsx';
import { useNavigate } from '@client/shared/router/router.tsx';
import { AuthGate, fetchSession, isOnboardingSession, isPlatformSession, SESSION_QUERY_KEY, stashGrant, UNAUTHENTICATED } from '@client/shared/auth/index.ts';
import {
  CALLBACK_DESTINATION,
  chooseDestination,
  isExpiredLinkOutcome,
  isSuccessOutcome,
  parseCallbackHash,
  postSessionFromEmail,
} from './auth-callback.ts';

export function AuthCallback() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [expired, setExpired] = useState(false);
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    void completeCallback({ navigate, queryClient, onExpired: () => setExpired(true) });
  }, [navigate, queryClient]);

  if (expired) return <ExpiredLink />;
  return <Splash label="Finishing sign-in…" />;
}

function ExpiredLink() {
  return (
    <AuthGate
      eyebrow="Sign-in"
      heading="This link is invalid or expired"
      intro="Sign-in links are single-use and time-limited. Ask your admin to resend your invite: the newest email always has a fresh link."
    />
  );
}

interface CompleteCallbackDeps {
  readonly navigate: (to: string, options?: { replace?: boolean }) => void;
  readonly queryClient: ReturnType<typeof useQueryClient>;
  readonly onExpired: () => void;
}

async function completeCallback({
  navigate,
  queryClient,
  onExpired,
}: CompleteCallbackDeps): Promise<void> {
  const hash = parseCallbackHash(window.location.hash);

  clearHash();

  if (!hash) {
    navigate(CALLBACK_DESTINATION.login, { replace: true });
    return;
  }

  let hasGroup = false;
  let destination: string;
  try {
    const outcome = await postSessionFromEmail(hash);

    if (isExpiredLinkOutcome(outcome)) {
      await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
      onExpired();
      return;
    }

    if (outcome.body?.passwordGrant) {
      stashGrant(outcome.body.passwordGrant);
    }

    if (isSuccessOutcome(outcome)) {
      // The mount-time `/auth/me` query fired BEFORE the POST set the cookies, so it's in flight (or
      // freshly cached) as unauthenticated. Cancel it so `fetchQuery` issues a FRESH request that
      // carries the new cookies instead of de-duping onto that stale, pre-cookie result: which
      // would misreport `hasGroup` and (for the recovery/invite branch) bounce the user to /login.
      // `staleTime: 0` covers the other half of the race: the pre-cookie 401 usually SETTLES before
      // the heavier POST returns, and under the default 30s staleTime `fetchQuery` would serve that
      // cached UNAUTHENTICATED without ever refetching (cancelQueries is a no-op on a settled query).
      await queryClient.cancelQueries({ queryKey: SESSION_QUERY_KEY });
      const session = await queryClient.fetchQuery({
        queryKey: SESSION_QUERY_KEY,
        queryFn: ({ signal }) => fetchSession(signal),
        staleTime: 0,
      });
      // Only a provisioned tenant (context) session has a group. An onboarding principal's /auth/me
      // is a 200 { onboarding: true } body: NOT unauthenticated, NOT platform: so it must be
      // excluded explicitly, or an invited owner is misrouted to the standalone set-password screen
      // and then asked for a password a second time in the wizard's Step 1.
      hasGroup =
        session !== UNAUTHENTICATED &&
        !isPlatformSession(session) &&
        !isOnboardingSession(session);
    } else {
      await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
    }

    destination = chooseDestination({ type: hash.type, outcome, hasGroup });
  } catch {
    destination = CALLBACK_DESTINATION.login;
  }

  navigate(destination, { replace: true });
}

function clearHash(): void {
  const { pathname, search } = window.location;
  window.history.replaceState(null, '', pathname + search);
}
